import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  // #handleVariantUpdate = (event) => {
  //   const source = event.detail.data.html;

  //   if (!source) return;
  //   const newMediaGallery = source.querySelector('media-gallery');

  //   if (!newMediaGallery) return;

  //   this.replaceWith(newMediaGallery);
  // };

#handleVariantUpdate = (event) => {
  const source = event.detail.data.html;

  if (!source) return;

  const newMediaGallery = source.querySelector('media-gallery');
  if (!newMediaGallery) return;

  // Get selected variant JSON
  const variantJson = source.querySelector(
    'variant-picker .variant-picker__form script[type="application/json"]'
  );

  let selectedAlt = '';

  if (variantJson) {
    try {
      const variant = JSON.parse(variantJson.textContent);

      selectedAlt =
        variant?.featured_media?.alt?.trim().toLowerCase() || '';

      console.log('Selected variant:', variant?.title);
      console.log('Selected ALT:', selectedAlt);
    } catch (error) {
      console.error('Variant JSON error:', error);
    }
  }

  // Filter gallery
  if (selectedAlt) {
    const slides = newMediaGallery.querySelectorAll(
      'slideshow-slide'
    );

    slides.forEach((slide) => {
      const img = slide.querySelector(
        '.product-media__image'
      );

      if (!img) return;

      const mediaAlt =
        img.getAttribute('alt')?.trim().toLowerCase() || '';

      const shouldShow = mediaAlt === selectedAlt;

      if (shouldShow) {
        slide.removeAttribute('hidden');
      } else {
        slide.setAttribute('hidden', '');
      }
    });

    // Filter thumbnails separately using thumbnail ALT
    const thumbnails = newMediaGallery.querySelectorAll(
      '.slideshow-controls__thumbnail'
    );

    thumbnails.forEach((thumbnail) => {
  const img = thumbnail.querySelector('img');

  if (!img) return;

  const mediaAlt =
    img.getAttribute('alt')?.trim().toLowerCase() || '';

  if (mediaAlt === selectedAlt) {
    thumbnail.style.removeProperty('display');
  } else {
    thumbnail.style.display = 'none';
  }
});


/* ===============================
   RE-INDEX VISIBLE THUMBNAILS
================================ */

const visibleThumbnails = Array.from(
  newMediaGallery.querySelectorAll(
    '.slideshow-controls__thumbnail'
  )
).filter((thumbnail) => thumbnail.style.display !== 'none');

visibleThumbnails.forEach((thumbnail, index) => {
  thumbnail.setAttribute(
    'on:click',
    `/select/${index}`
  );

  thumbnail.setAttribute(
    'aria-selected',
    index === 0 ? 'true' : 'false'
  );
});


/* ===============================
   RESET SLIDESHOW TO FIRST IMAGE
================================ */

const slideshow =
  newMediaGallery.querySelector('slideshow-component');

if (slideshow) {
  slideshow.setAttribute('initial-slide', '0');
}
  }

  this.replaceWith(newMediaGallery);
};

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
